import { useEffect, useMemo, useState } from "react";
import Layout, { PageHeader, Card } from "../components/Layout";
import { api } from "../lib/api";
import { Calendar as CalIcon, MapPin, RefreshCw, AlertCircle, ExternalLink, Video, Users, ChevronDown, ChevronUp } from "lucide-react";

function fmtTime(iso, allDay) {
  if (allDay) return "All day";
  const d = new Date(iso);
  return d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: false });
}
function dayKey(iso) {
  return new Date(iso).toLocaleDateString("en-IN", { weekday: "long", day: "2-digit", month: "long", year: "numeric" });
}
function isToday(iso) {
  const d = new Date(iso);
  const n = new Date();
  return d.toDateString() === n.toDateString();
}

// Escape HTML to avoid XSS, then linkify URLs in description text
const URL_RE = /(https?:\/\/[^\s<>"']+)/g;
function linkifyDescription(text) {
  if (!text) return null;
  const parts = text.split(URL_RE);
  return parts.map((part, i) => {
    if (URL_RE.test(part)) {
      // reset regex global state (test() advances lastIndex)
      URL_RE.lastIndex = 0;
      const isMeet = part.includes("meet.google.com");
      return (
        <a
          key={i}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          className={`underline underline-offset-2 break-all ${isMeet ? "text-emerald-400 hover:text-emerald-300" : "text-sky-400 hover:text-sky-300"}`}
          onClick={(e) => e.stopPropagation()}
        >
          {part}
        </a>
      );
    }
    URL_RE.lastIndex = 0;
    return <span key={i}>{part}</span>;
  });
}

function StatusDot({ status }) {
  const s = (status || "").toLowerCase();
  const color = s === "accepted" ? "bg-emerald-500" : s === "declined" ? "bg-rose-500" : s === "tentative" ? "bg-amber-500" : "bg-zinc-600";
  return <span className={`inline-block h-1.5 w-1.5 rounded-full ${color}`} />;
}

function EventRow({ e }) {
  const [expanded, setExpanded] = useState(false);
  const hasDetails = !!(e.description || (e.attendees && e.attendees.length > 0) || e.meet_url);

  return (
    <Card className="p-4 hover:bg-[#161616] transition-colors" testId={`cal-event-${e.id}`}>
      <div className="flex items-start gap-4">
        <div className="w-20 shrink-0 text-right">
          <div className="font-display text-base tabular text-emerald-400">{fmtTime(e.start, e.all_day)}</div>
          {!e.all_day && e.end && (
            <div className="text-[10px] text-zinc-500 tabular">→ {fmtTime(e.end, false)}</div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-medium text-sm">{e.title}</div>
          <div className="flex items-center gap-3 mt-1 text-xs text-zinc-500 flex-wrap">
            <span className="truncate max-w-[200px]">{e.calendar}</span>
            {e.location && (
              <span className="inline-flex items-center gap-1 truncate max-w-[260px]">
                <MapPin className="h-3 w-3" /> {e.location}
              </span>
            )}
            {e.attendees && e.attendees.length > 0 && (
              <span className="inline-flex items-center gap-1 text-zinc-500">
                <Users className="h-3 w-3" /> {e.attendees.length}
              </span>
            )}
          </div>

          {/* Quick actions */}
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            {e.meet_url && (
              <a
                href={e.meet_url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(ev) => ev.stopPropagation()}
                data-testid={`cal-meet-${e.id}`}
                className="inline-flex items-center gap-1.5 text-[11px] px-2 py-1 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 transition-colors"
              >
                <Video className="h-3 w-3" /> Join Meet
              </a>
            )}
            {hasDetails && (
              <button
                onClick={() => setExpanded((x) => !x)}
                data-testid={`cal-toggle-${e.id}`}
                className="inline-flex items-center gap-1 text-[11px] text-zinc-400 hover:text-zinc-200"
              >
                {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                {expanded ? "Hide details" : "Details"}
              </button>
            )}
          </div>
        </div>
      </div>

      {expanded && (
        <div className="mt-3 pl-24 border-t border-zinc-900 pt-3 space-y-3">
          {e.description && (
            <div>
              <div className="overline text-zinc-500 mb-1">Description</div>
              <div className="text-xs text-zinc-300 whitespace-pre-wrap break-words leading-relaxed">
                {linkifyDescription(e.description)}
              </div>
            </div>
          )}
          {e.attendees && e.attendees.length > 0 && (
            <div>
              <div className="overline text-zinc-500 mb-1.5">Attendees ({e.attendees.length})</div>
              <div className="flex flex-wrap gap-1.5">
                {e.attendees.map((a, i) => (
                  <span
                    key={i}
                    className="inline-flex items-center gap-1.5 text-[11px] bg-[#181818] border border-zinc-800 px-2 py-1"
                    title={a.status || ""}
                  >
                    <StatusDot status={a.status} />
                    <span className="text-zinc-200">{a.name || a.email}</span>
                    {a.name && a.email && a.name !== a.email && (
                      <span className="text-zinc-500">·</span>
                    )}
                    {a.name && a.email && a.name !== a.email && (
                      <a
                        href={`mailto:${a.email}`}
                        onClick={(ev) => ev.stopPropagation()}
                        className="text-zinc-500 hover:text-zinc-300"
                      >
                        {a.email}
                      </a>
                    )}
                  </span>
                ))}
              </div>
              {e.organizer && (
                <div className="text-[11px] text-zinc-500 mt-2">
                  Organizer: <span className="text-zinc-300">{e.organizer_name || e.organizer}</span>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

export default function Calendar() {
  const [events, setEvents] = useState([]);
  const [errors, setErrors] = useState([]);
  const [fetchedAt, setFetchedAt] = useState(null);
  const [loading, setLoading] = useState(false);
  const [calCount, setCalCount] = useState(0);

  const reload = async (refresh = false) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/calendar/upcoming?limit=100${refresh ? "&refresh=true" : ""}`);
      setEvents(data.events || []);
      setErrors(data.errors || []);
      setFetchedAt(data.fetched_at);
      setCalCount(data.calendar_count || 0);
    } finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, []);

  const grouped = useMemo(() => {
    const out = [];
    let last = null;
    for (const e of events) {
      const k = dayKey(e.start);
      if (k !== last) { out.push({ day: k, items: [] }); last = k; }
      out[out.length - 1].items.push(e);
    }
    return out;
  }, [events]);

  return (
    <Layout>
      <PageHeader
        overline={`${calCount} calendar${calCount === 1 ? "" : "s"} · auto-refreshes every 5 min`}
        title="Calendar"
        subtitle="Upcoming meetings & events for the next 12 months. Pulled from your Google Calendar iCal feeds."
        actions={
          <button
            onClick={() => reload(true)}
            disabled={loading}
            data-testid="cal-refresh"
            className="inline-flex items-center gap-2 border border-zinc-800 text-zinc-200 hover:bg-[#181818] disabled:opacity-40 text-sm px-4 py-2 transition-colors"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> {loading ? "Refreshing…" : "Refresh"}
          </button>
        }
        testId="calendar-header"
      />

      {errors.length > 0 && (
        <Card className="p-4 mb-6 border-amber-500/30" testId="cal-errors">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-4 w-4 text-amber-400 mt-0.5" />
            <div className="text-sm">
              <div className="overline mb-1 text-amber-300">Some calendars couldn&apos;t be loaded</div>
              {errors.map((e, i) => <div key={i} className="text-amber-100/70 text-xs">{e}</div>)}
              <div className="text-xs text-zinc-500 mt-2">
                If a feed returns 404, make sure that calendar is shared publicly OR use the &ldquo;Secret address in iCal format&rdquo; instead.
              </div>
            </div>
          </div>
        </Card>
      )}

      {events.length === 0 && !loading && (
        <Card className="p-12 text-center border-dashed border-zinc-800">
          <CalIcon className="h-10 w-10 text-zinc-700 mx-auto mb-3" />
          <div className="font-display text-xl mb-1">No upcoming events</div>
          <div className="text-sm text-zinc-500">Nothing scheduled in the next 60 days, or the iCal feed is empty.</div>
        </Card>
      )}

      {grouped.map((g) => (
        <section key={g.day} className="mb-8" data-testid={`cal-day-${g.day}`}>
          <div className="flex items-baseline gap-3 mb-3">
            <h3 className="font-display text-lg">{g.day}</h3>
            {isToday(g.items[0].start) && (
              <span className="overline text-emerald-400">Today</span>
            )}
            <span className="overline text-zinc-600">{g.items.length} event{g.items.length === 1 ? "" : "s"}</span>
          </div>
          <div className="space-y-2">
            {g.items.map((e) => (
              <EventRow key={e.id} e={e} />
            ))}
          </div>
        </section>
      ))}

      {fetchedAt && (
        <div className="mt-8 text-xs text-zinc-600 flex items-center gap-2">
          <ExternalLink className="h-3 w-3" />
          Last fetched {new Date(fetchedAt).toLocaleString("en-IN")} · 5 min cache
        </div>
      )}
    </Layout>
  );
}
